import { useEffect, useMemo } from 'react';
import { useAppSelector } from '../../../../hook/useAppSelector';
import { isGeoColumn } from '../../../../util/table-helper';
import { useGeoFilter } from '../../../../hook/useGeoFilter';
import { t } from 'i18next';
import FilterSelectAll from './FilterSelectAll';
import OptionsList from './OptionsList';
import useGeoSelectionMessage from '../../../../hook/useGeoSelectionMessage';
import { Trans } from 'react-i18next';
import Search from '../../Common/Search';
import { useLocation } from 'react-router-dom';
import { GeoClass } from '../../../../interface/response/GeoFiltersResponse';
import { CASH_REGISTER_URL } from '../../../../util/url';
import { GeoColumns } from '../../../../enum/GeoDivisionColumnName';

interface GeoFiltersProps {
  toggleDropdown: (type: string, index: number) => void;
  isOpen: (type: string, index: number) => boolean;
  filters: any;
  setFilters: (newFilters: {}) => void;
  justClosed: { type: string; index: number } | null;
}

const GeoFilters: React.FC<GeoFiltersProps> = ({ toggleDropdown, isOpen, filters, setFilters, justClosed }) => {
  const getMessage = useGeoSelectionMessage();

  const allOptsFull = useAppSelector((state) => state.filters.allOptionsFull) || {};

  const allOptsFilteredFromStore = useAppSelector((state) => state.filters.allOptionsFiltered);

  const allOptsFiltered = useMemo(() => {
    return allOptsFilteredFromStore || {};
  }, [allOptsFilteredFromStore]);

  const geoFiltersData = useAppSelector((state) => state.filters.geoFiltersData) || {};

  const columns = Object.keys(geoFiltersData).filter((column: string) => isGeoColumn(column));
  const location = useLocation();
  const currentPath = location.pathname;
  const { handleChange, handleDropdownOpen, selectAll, numberOfSelected, filteredGeoData, onFilteredData, isDisabled } =
    useGeoFilter(filters, setFilters, columns, allOptsFiltered);

  useEffect(() => {
    if (!filters) return;

    let changed = false;
    const newFilters: {
      [key: string]: string;
    } = {};

    columns.forEach((col) => {
      const validOptions = allOptsFiltered[col];
      const selected = filters[col] || [];

      if (!validOptions || validOptions.length === 0) {
        newFilters[col] = selected;
        return;
      }

      const filteredSelection = selected.filter((sel: GeoClass) =>
        validOptions.some((opt: GeoClass) => opt.ID === sel.ID),
      );

      newFilters[col] = filteredSelection;

      if (
        selected.length !== filteredSelection.length ||
        !selected.every((sel: GeoClass, i: number) => sel.ID === filteredSelection[i]?.ID)
      ) {
        changed = true;
      }
    });

    if (changed) {
      setFilters(newFilters);
    }
  }, [allOptsFiltered, columns, filters, setFilters]);

  return (
    <div className="geo_filters filters">
      {columns.map((col: any, index) => {
        const { key, values } = getMessage(col, numberOfSelected[col]);

        const visibleOptions = allOptsFiltered[col] || [];

        const isFirst = col === columns[0];
        const options = (isFirst ? allOptsFull[col] : allOptsFiltered[col]) || [];

        const isColDisabled = isDisabled(col);
        const isDropdownOpen = isOpen(t('statisticPage.header.filter.dropdown.geo'), index);
        const isDropdownClose =
          !isDisabled(col) &&
          !isDropdownOpen &&
          justClosed?.index === index &&
          justClosed?.type === t('statisticPage.header.filter.dropdown.geo');

        return (
          <div
            key={`${col}-${index}`}
            onClick={() => {
              toggleDropdown(t('statisticPage.header.filter.dropdown.geo'), index);
              handleDropdownOpen(col);
            }}
            className={`filter_select ${isColDisabled ? 'disabled' : ''} ${
              isDropdownOpen && !isColDisabled ? 'opened' : ''
            } ${isDropdownClose ? 'closed' : ''} ${numberOfSelected[col] > 0 ? 'expanded' : ''}`}
          >
            <span className={`dropdown_title ${!isDropdownOpen || isColDisabled ? 'with_padding' : ''}`}>
              <Trans i18nKey={key} values={values} components={{ count: <span className="number_of_selected" /> }} />
            </span>

            <div className="options_container" onClick={(e) => e.stopPropagation()}>
              <Search
                placeholder={t('general.search')}
                className="geo_filter_search"
                data={options}
                searchKeys={[t('statisticPage.header.filter.dropdown.geoFilters.name')]}
                onFilteredData={onFilteredData[col]}
              />
              <div className="options_list">
                {currentPath !== CASH_REGISTER_URL ? (
                  <FilterSelectAll
                    className="select_all_wrapper"
                    checked={
                      ((filteredGeoData[col] as GeoClass[])?.length ?? 0) > 0 &&
                      (filteredGeoData[col] as GeoClass[])?.every((opt: GeoClass) =>
                        (filters[col] || []).some((f: GeoClass) => f.ID === opt.ID),
                      )
                    }
                    selectAll={() => selectAll(col, filteredGeoData[col])}
                    title={t('statisticPage.header.filter.dropdown.selectAll')}
                  />
                ) : col !== GeoColumns.GROUP ? (
                  <FilterSelectAll
                    className="select_all_wrapper"
                    checked={
                      ((filteredGeoData[col] as GeoClass[])?.length ?? 0) > 0 &&
                      (filteredGeoData[col] as GeoClass[])?.every((opt: GeoClass) =>
                        (filters[col] || []).some((f: GeoClass) => f.ID === opt.ID),
                      )
                    }
                    selectAll={() => selectAll(col, filteredGeoData[col])}
                    title={t('statisticPage.header.filter.dropdown.selectAll')}
                  />
                ) : (
                  ''
                )}
                <OptionsList
                  checkbox={true}
                  column={col}
                  filters={filters}
                  handleChange={handleChange}
                  filteredOptions={filteredGeoData[col] || visibleOptions}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GeoFilters;
